import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Bell, BellOff, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

export default function NotificationSettings() {
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    requestPermission,
    subscribe, 
    unsubscribe,
    showLocalNotification 
  } = usePushNotifications();
  
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast.info('Notifications disabled');
      } else {
        const success = await subscribe();
        if (success) {
          toast.success('Notifications enabled! 🔔');
          // Show a test notification
          setTimeout(() => {
            showLocalNotification('Welcome to Savecation! 🌴', {
              body: 'You\'ll now receive updates about your savings goals!',
              tag: 'welcome'
            });
          }, 1000);
        } else {
          toast.error('Could not enable notifications');
        }
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotification = () => {
    showLocalNotification('Test Notification 🎉', {
      body: 'Your notifications are working perfectly!',
      tag: 'test'
    });
  };

  if (!isSupported) {
    return (
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
            <BellOff className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Push Notifications</p>
            <p className="text-xs text-slate-500">Not supported on this device</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isSubscribed ? 'bg-sky-100 text-sky-600' : 'bg-slate-100 text-slate-600'
          }`}>
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">Goal Notifications</p>
            <p className="text-xs text-slate-500">
              {isSubscribed 
                ? 'Get notified about milestones' 
                : 'Enable to receive updates'}
            </p>
          </div>
        </div>
        <Switch 
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading}
          data-testid="notification-toggle"
        />
      </div>

      {isSubscribed && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleTestNotification}
          className="w-full rounded-full"
          data-testid="test-notification-btn"
        >
          <Bell className="w-4 h-4 mr-2" /> Test Notification
        </Button>
      )}

      {permission === 'denied' && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl">
          <p className="text-sm text-rose-700">
            Notifications are blocked. Please enable them in your browser settings to receive goal updates.
          </p>
        </div>
      )}
    </div>
  );
}
